import React, { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { Download, Calendar } from "lucide-react";
import { newsletterIssues } from "../data/newsletterIssues";
import { NewsletterSignup } from "../components/NewsletterSignup";

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
};

const AnimatedSection = ({ children, className = "" }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <motion.section
      ref={ref}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      variants={staggerContainer}
      className={className}
    >
      {children}
    </motion.section>
  );
};

// Newsletter Issue Card
const IssueCard = ({ issue }) => {
  return (
    <motion.div
      variants={fadeInUp}
      data-testid={`newsletter-issue-card-${issue.id}`}
      className="card-light rounded-2xl overflow-hidden group flex flex-col"
    >
      <div className="aspect-[4/5] overflow-hidden bg-slate-100">
        <img
          src={issue.cover}
          alt={issue.title}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          loading="lazy"
        />
      </div>
      <div className="p-6 flex flex-col flex-1">
        <div className="flex items-center gap-2 text-slate-400 text-xs font-body mb-2">
          <Calendar size={14} />
          <span>{issue.date}</span>
        </div>
        <h3 className="font-heading text-xl text-slate-800 mb-2">{issue.title}</h3>
        <p className="text-slate-500 text-sm font-body mb-6 flex-1">{issue.description}</p>
        <a
          href={issue.file}
          download
          data-testid={`newsletter-download-btn-${issue.id}`}
          className="btn-gradient inline-flex items-center justify-center gap-2 text-sm"
        >
          <Download size={16} /> Download
        </a>
      </div>
    </motion.div>
  );
};

const NewsletterPage = () => {
  return (
    <div className="pt-24">
      {/* Hero */}
      <AnimatedSection className="section-padding bg-gradient-to-br from-yellow-50 via-white to-blue-50">
        <div className="container-custom">
          <motion.div variants={fadeInUp} className="max-w-4xl mx-auto text-center">
            <span className="font-accent text-red-600 text-sm tracking-[0.2em] uppercase">GSLC Newsletter</span>
            <h1 className="font-heading text-4xl md:text-6xl font-semibold mt-4 mb-6 text-slate-800">
              ImpAct — GSLC’s <span className="gradient-text">Education Newsletter</span>
            </h1>
            <p className="text-lg text-slate-600 leading-relaxed">
              Insights, stories, and updates from the world of education — every 15 days.
            </p>
          </motion.div>
        </div>
      </AnimatedSection>

      {/* Issues Grid */}
      <AnimatedSection className="section-padding">
        <div className="container-custom">
          <div
            data-testid="newsletter-issues-grid"
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8"
          >
            {newsletterIssues.map((issue) => (
              <IssueCard key={issue.id} issue={issue} />
            ))}
          </div>
        </div>
      </AnimatedSection>

      {/* Email Signup - reused homepage component */}
      <NewsletterSignup />
    </div>
  );
};

export default NewsletterPage;
